import { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
    Cog8ToothIcon,
    EnvelopeOpenIcon,
    ShareIcon
} from "@heroicons/react/24/outline";
import { TbCalendar } from "react-icons/tb";

import {
    selectIsSidebarLarge,
    selectMemberships,
    selectUser
} from "../lib/scraper.slice";
import {
    ENDPOINT_TYPE,
    ORG_TYPES,
    USER_ROLES
} from "../lib/consts";
import { EndpointType } from "../lib/types";
import { classNames } from "../lib/utils";
import { ConnectorType } from "../lib/backend/extractions.types.generated";

import { Button } from "../components/Button";
import {
    WizardButtonIcon,
    WizardLogoButtonImage
} from "../components/WizardButton";
import { WarningModal } from "../components/WarningModal";
import MsGraphApiIcon from "../icons/MsGraphApiIcon";

export const wizard_new_email_endpoint_roles = [USER_ROLES.admin, USER_ROLES.enterprise, USER_ROLES.professional, USER_ROLES.professional_plus];
export const wizard_new_rest_api_endpoint_roles = [USER_ROLES.admin, USER_ROLES.enterprise];
export const wizard_new_email_digest_connector_roles = [USER_ROLES.admin, USER_ROLES.enterprise];
export const wizard_new_webhook_connector_roles = [USER_ROLES.admin, USER_ROLES.enterprise];
export const wizard_new_microsoft_graph_api_connector_roles = [USER_ROLES.admin, USER_ROLES.enterprise];

export const wizard_new_email_endpoint_warning_text = "Email connectors are only available to our paying clients";
export const wizard_new_rest_api_endpoint_warning_text = "Rest API connectors are only available to our Enterprise clients";
export const wizard_new_email_digest_connector_warning_text = "Digest Email connectors are only available to our Enterprise clients";
export const wizard_new_webhook_connector_warning_text = "Webhook connectors are only available to our Enterprise clients";
export const wizard_new_microsoft_graph_api_connector_warning_text = "Microsoft Graph API connectors are only available to our Enterprise clients with Microsoft SSO enabled";

export function NewConnectorInit() {
    const navigate = useNavigate();

    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const user = useSelector(selectUser);
    const memberships = useSelector(selectMemberships);

    const [warning_modal_text, setWarningModalText] = useState<string | undefined>(undefined);

    // trail account
    const is_free = user.role === USER_ROLES.free;
    const personal_org = memberships.find((membership) => membership.org.type === ORG_TYPES.personal);
    const subscribe_url = personal_org ? `/stripe-subscribe/${personal_org.org.uuid}` : "/";

    const contactSales = () => {
        // redirect to home page
        window.location.href = "https://meetings-eu1.hubspot.com/tomaz/book-a-demo";
    };

    const selectEndpointWizard = (type: EndpointType, allowed_roles: string[], warning_text: string) => {
        if (!allowed_roles.includes(user.role as string)) {
            setWarningModalText(warning_text);
            return;
        }
        navigate(`/endpoint/new/${type}`);
    }

    const selectConnectorWizard = (type: ConnectorType, allowed_roles: string[], warning_text: string) => {
        if (!allowed_roles.includes(user.role as string)) {
            setWarningModalText(warning_text);
            return;
        }
        navigate(`/connector/new/${type}`);
    }

    return <div className={classNames("lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        <div className={classNames("z-50 h-16 bg-white border-b-sea_blue-700 border-b lg:fixed lg:right-0", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
            <div className="px-10 py-4 lg:max-w-5xl">
                <div className="flex flex-row items-center">
                    <h2 className="text-xl font-semibold leading-7 text-gray-900 sm:truncate sm:text-2xl sm:tracking-tight">
                        Create new Connector
                    </h2>
                </div>
            </div>
        </div>

        <div className="p-6 mt-10 lg:pt-12">
            {is_free && <div className="mx-2 mt-2 p-2 text-lg bg-candy_corn-50 border rounded-lg">
                <div className="px-5 py-2 flex flex-row items-center">
                    <p>This is a free trial account. Connectors are only available in our paid plans.</p>
                    <div className="flex-grow" />
                    <Button text="Upgrade" href={subscribe_url} />
                </div>
            </div>}

            <div className="px-6 py-8 text-gray-600">
                Select a connector template to get started.
            </div>
            <div className="pb-12 flex flex-wrap">
                <div className="opacity-100">
                    <WizardButtonIcon title="Email" icon={EnvelopeOpenIcon}
                        tooltip={!wizard_new_email_endpoint_roles.includes(user.role) ? "Only available to our paying clients" : undefined}
                        locked={!wizard_new_email_endpoint_roles.includes(user.role)}
                        onClick={() => selectEndpointWizard(ENDPOINT_TYPE.email, wizard_new_email_endpoint_roles, wizard_new_email_endpoint_warning_text)} />
                </div>
                <div className="opacity-100">
                    <WizardButtonIcon title="Rest API" icon={Cog8ToothIcon}
                        tooltip={!wizard_new_rest_api_endpoint_roles.includes(user.role) ? "Only available to our Enterprise clients" : undefined}
                        locked={!wizard_new_rest_api_endpoint_roles.includes(user.role)}
                        onClick={() => selectEndpointWizard(ENDPOINT_TYPE.rest_api, wizard_new_rest_api_endpoint_roles, wizard_new_rest_api_endpoint_warning_text)} />
                </div>
                <div className="opacity-100">
                    <WizardButtonIcon title="Digest Email" icon={TbCalendar}
                        tooltip={!wizard_new_email_digest_connector_roles.includes(user.role) ? "Only available to our Enterprise clients" : undefined}
                        locked={!wizard_new_email_digest_connector_roles.includes(user.role)}
                        onClick={() => selectConnectorWizard("email_digest", wizard_new_email_digest_connector_roles, wizard_new_email_digest_connector_warning_text)} />
                </div>
                <div className="opacity-100">
                    <WizardButtonIcon title="Webhook" icon={ShareIcon}
                        tooltip={!wizard_new_webhook_connector_roles.includes(user.role) ? "Only available to our Enterprise clients" : undefined}
                        locked={!wizard_new_webhook_connector_roles.includes(user.role)}
                        onClick={() => selectConnectorWizard("webhook", wizard_new_webhook_connector_roles, wizard_new_webhook_connector_warning_text)} />
                </div>
                <div className="opacity-100">
                    <WizardButtonIcon title="Microsoft Graph API" icon={MsGraphApiIcon}
                        tooltip={!wizard_new_microsoft_graph_api_connector_roles.includes(user.role) ? "Only available to our Enterprise clients with Microsoft SSO enabled" : undefined}
                        locked={!wizard_new_microsoft_graph_api_connector_roles.includes(user.role)}
                        onClick={() => selectConnectorWizard("microsoft_graph_api", wizard_new_microsoft_graph_api_connector_roles, wizard_new_microsoft_graph_api_connector_warning_text)} />
                </div>
            </div>
            <div className="flex flex-wrap overflow-y-auto">
                <div className="opacity-100">
                    <WizardLogoButtonImage title="SAP S/4 HANA" image="/wizards/new_integration_sap_hana.png" onClick={contactSales} />
                </div>
                <div className="opacity-100">
                    <WizardLogoButtonImage title="Dynamics 365" image="/wizards/new_integration_microsoft_dynamics_365.png" onClick={contactSales} />
                </div>
                <div className="opacity-100">
                    <WizardLogoButtonImage title="NetSuite" image="/wizards/new_integration_oracle_netsuite.jpeg" onClick={contactSales} />
                </div>
                <div className="opacity-100">
                    <WizardLogoButtonImage title="Workday" image="/wizards/new_integration_workday.png" onClick={contactSales} />
                </div>
                <div className="opacity-100">
                    <WizardLogoButtonImage title="Xero" image="/wizards/new_integration_xero.png" onClick={contactSales} />
                </div>
                <div className="opacity-100">
                    <WizardLogoButtonImage title="QuickBooks" image="/wizards/new_integration_quickbooks.svg" onClick={contactSales} />
                </div>
                <div className="opacity-100">
                    <WizardLogoButtonImage title="SalesForce" image="/wizards/new_integration_salesforce.png" onClick={contactSales} />
                </div>
            </div>
        </div>

        <WarningModal
            open={warning_modal_text !== undefined}
            title="Warning"
            message={[
                warning_modal_text,
                <Fragment key="contact_sales">Please <a href="https://meetings-eu1.hubspot.com/tomaz/book-a-demo" className="text-space_blue-600" target="_blank" rel="noreferrer">contact our team</a> for more information on how to upgrade.</Fragment>
            ]}
            onClose={() => setWarningModalText(undefined)}
        />

    </div>;
}
